import { useState } from 'react';

import cn from 'classnames';
import { useDispatch } from 'react-redux';

import { getKycStatus, updateProfile } from 'actions/user';
import Render from 'components/render';
import { STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { dayjs } from 'helpers/day';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { isEmpty, parseErrorMsg } from 'helpers/utils';
import useLocalStorage from 'hooks/useLocalStorage';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import { allOpenPositionsSelector } from 'selectors/holdingsSelectors';
import { userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { ArrowDown } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import MaterialConfirmDialog from 'UIKit/MaterialConfirmDialog';

import IncomeDropDown from './IncomeDropDown';
import InfoBox from './InfoBox';
import styles from './mandatoryDeclarationModal.module.scss';
import OccupationDropDown from './OccupationDropDown';
import StateDropDown from './StateDropDown';

const mandatoryDeclarationModal = i18n.t('account:kycConcent.mandatoryDeclaration');
const infoText = i18n.t('account:whyThisInfo');
const submitButton = i18n.t('common:submit');

const MandatoryDeclarationModal = () => {
  const dispatch = useDispatch();
  const { whenBelowMd: isMobile } = useCustomMediaQuery();
  const [stateOfResidence, setStateOfResidence] = useState('');
  const [occupationOfUser, setOccupationOfUser] = useState('');
  const [incomeOfUser, setIncomeOfUser] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [, setStateOfResidenceModalCloseTime] = useLocalStorage(
    STORAGE_KEYS.STATE_OF_RESIDENCE_MODAL_CLOSED_TIME,
    ''
  );
  const user = useAppSelector(userSelector);
  const { id, email } = user;

  const openPositions = useAppSelector(allOpenPositionsSelector);
  const isPositionsEmpty = openPositions.length === 0;

  const onStateOptionClicked = state => {
    setStateOfResidence(state);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_STATE_SELECTED, {
      state,
    });
  };

  const onOccupationOptionClicked = userOccupation => {
    setOccupationOfUser(userOccupation);
    trackMixpanelEvent(
      MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_OCCUPATION_SELECTED,
      {
        userOccupation,
      }
    );
  };

  const onIncomeOptionClicked = userIncome => {
    setIncomeOfUser(userIncome);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_INCOME_SELECTED, {
      userIncome,
    });
  };

  const toggleInfo = () => {
    setShowInfo(!showInfo);
  };

  const handleClose = () => {
    setIsOpen(false);
    const currentTime = dayjs().valueOf(); // Get current timestamp in milliseconds
    setStateOfResidenceModalCloseTime(currentTime);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_CLOSED);
  };

  const onSubmit = async () => {
    if (stateOfResidence || occupationOfUser || incomeOfUser) {
      trackMixpanelEvent(
        MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_STATE_SUBMITTED
      );
      try {
        setLoading(true);
        // Initialize params with user_id and conditionally add other properties
        const params = {
          user_id: id,
          ...(stateOfResidence && { region: stateOfResidence }),
          ...(occupationOfUser && { occupation: occupationOfUser }),
          ...(incomeOfUser && { income: incomeOfUser }),
        };

        const updateProfileCall = await dispatch(updateProfile(params));

        if (updateProfileCall.success) {
          trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_UPDATED, {
            state: stateOfResidence || '',
            occupation: occupationOfUser || '',
            income: incomeOfUser || '',
          });
          await dispatch(getKycStatus());
        }
        setIsOpen(false);
      } catch (profileUpdateError) {
        const errorObj = parseErrorMsg(profileUpdateError);
        const errorCode = errorObj?.error?.code ?? 'defaultMessage';
        setErrorMessage(errorCode);
        trackMixpanelEvent(
          MIXPANEL_EVENT_NAMES.MANDATORY_DECLARATION_POPUP_UPDATE_FAILED,
          {
            errorCode,
            id,
            email,
          }
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Render and confirm button disablitly conditions
  const isOccupationAndIncomeNull = user.occupation === null && user.income === null;
  const isRegionNull = user.region === null;
  const isOccupationNull = user.occupation === null;
  const isIncomeNull = user.income === null;
  const isRegionOccupationAndIncomeNull =
    user.region === null && user.occupation === null && user.income === null;
  const disableConfirmButton =
    // Disable if region is null and no state of residence is selected
    (user.region == null && !stateOfResidence) ||
    // Disable if occupation is null and no occupation is selected
    (user.occupation == null && !occupationOfUser) ||
    // Disable if income is null and no income is selected
    (user.income == null && !incomeOfUser) ||
    // Disable if loading is true (submission in progress)
    loading ||
    // Disable if there is an error message
    errorMessage;

  return (
    <MaterialConfirmDialog
      showCrossIcon={!isPositionsEmpty}
      open={isOpen}
      onClose={handleClose}
      variant="v2"
      fullWidth={isMobile}
      disableConfirmButton={disableConfirmButton}
      headerClassName={styles.modalHeader}
      containerClassName={styles.modalContainer}
      footerClassName={styles.modalFooter}
      title={mandatoryDeclarationModal}
      titleClassName={styles.modalTitleClassName}
      confirmButtonText={submitButton}
      confirmClassName={styles.agreeButton}
      contentClassName="disable-select"
      onConfirm={onSubmit}
      bodyClassName={styles.modalBody}
      testId="mandatoryDeclarationModal"
      rootClassName={styles.rootClassName}
      closeIconClassName={styles.closeIconClassName}
    >
      <Render when={isRegionNull}>
        <StateDropDown
          selectedValue={stateOfResidence}
          errorMessage={errorMessage}
          onStateOptionClicked={onStateOptionClicked}
        />
      </Render>

      <Render when={isOccupationNull}>
        <OccupationDropDown
          selectedValue={occupationOfUser}
          errorMessage={errorMessage}
          onOccupationOptionClicked={onOccupationOptionClicked}
        />
      </Render>

      <Render when={isIncomeNull}>
        <IncomeDropDown
          selectedValue={incomeOfUser}
          errorMessage={errorMessage}
          onIncomeOptionClicked={onIncomeOptionClicked}
        />
      </Render>

      <Box
        as="p"
        data-testid="mandatoryDeclarationErrorMessage"
        renderWhen={!isEmpty(errorMessage)}
        className={styles.errorMessage}
      >
        {i18n.t(`errors:custom.${errorMessage}`)}
      </Box>

      <Box
        fontSize="sm2"
        textColor="secondary"
        className={styles.infoText}
        data-testid="infoText"
        onClick={toggleInfo}
      >
        {infoText}

        <ArrowDown
          className={cn({
            [styles.arrowUp]: showInfo,
            [styles.arrowDown]: !showInfo,
          })}
        />
      </Box>

      <InfoBox
        showInfo={showInfo}
        isRegionNull={isRegionNull}
        isOccupationAndIncomeNull={isOccupationAndIncomeNull}
        isRegionOccupationAndIncomeNull={isRegionOccupationAndIncomeNull}
      />
    </MaterialConfirmDialog>
  );
};

export default MandatoryDeclarationModal;
